import React, { useEffect, useState } from 'react';
import './courseKit.css';

const pad = (n) => String(n).padStart(2, '0');

const remaining = (endsAt) => {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return {
    days: Math.floor(ms / 86400000),
    hours: Math.floor((ms % 86400000) / 3600000),
    minutes: Math.floor((ms % 3600000) / 60000),
    seconds: Math.floor((ms % 60000) / 1000),
  };
};

const wordPart = (value, singular, plural) => `${value} ${value === 1 ? singular : plural}`;

/**
 * Live countdown to a REAL promotion deadline from the promotions table.
 * Renders nothing once the deadline passes, so an expired sale can never keep
 * showing a fake timer.
 *
 * `label` should name what ends (e.g. "Super Early Bird pricing ends in").
 * `variant="words"` renders "33 days, 3 hours, 11 minutes, 8 seconds".
 * `variant="blocks"` renders day/hour/min/sec tiles for sales surfaces.
 * `units` narrows the blocks to the ones a surface wants, e.g. ['days',
 * 'hours'] for a deadline two weeks out, where a ticking seconds tile is
 * urgency the date does not have. The clock only ticks as fast as the smallest
 * unit on screen.
 */
const BLOCK_UNITS = ['days', 'hours', 'minutes', 'seconds'];

const PromoCountdown = ({
  endsAt,
  label = 'Pricing ends in',
  units = BLOCK_UNITS,
  variant = 'words',
}) => {
  const [left, setLeft] = useState(() => remaining(endsAt));
  // A string, not the array, so a caller writing the list inline does not
  // restart the clock on every render.
  const shown = units.join(',');

  useEffect(() => {
    if (!endsAt) return undefined;
    setLeft(remaining(endsAt));
    const parts = shown.split(',');
    let period = 1000;
    if (variant === 'days') period = 3600000;
    else if (variant === 'blocks' && !parts.includes('seconds')) {
      period = parts.includes('minutes') ? 15000 : 60000;
    }
    const id = setInterval(() => {
      const next = remaining(endsAt);
      setLeft(next);
      if (!next) clearInterval(id);
    }, period);
    return () => clearInterval(id);
  }, [endsAt, shown, variant]);

  if (!endsAt || !left) return null;

  if (variant === 'days') {
    return (
      <div className="ck-countdown ck-countdown--days">
        {label ? <span className="ck-countdown-label">{label}</span> : null}
        <span className="ck-countdown-words" data-testid="countdown-days">
          {wordPart(left.days, 'day', 'days')}
        </span>
      </div>
    );
  }

  if (variant === 'blocks') {
    const parts = [
      { key: 'days', value: left.days, unit: left.days === 1 ? 'day' : 'days' },
      { key: 'hours', value: left.hours, unit: left.hours === 1 ? 'hour' : 'hours' },
      { key: 'minutes', value: left.minutes, unit: left.minutes === 1 ? 'minute' : 'minutes' },
      { key: 'seconds', value: left.seconds, unit: left.seconds === 1 ? 'second' : 'seconds' },
    ].filter((part) => shown.split(',').includes(part.key));

    return (
      <div className="ck-countdown ck-countdown--blocks" role="timer" aria-live="off">
        {label ? <p className="ck-countdown-label">{label}</p> : null}
        <div className="ck-countdown-blocks" aria-label={label || 'Countdown'}>
          {parts.map((part) => (
            <div key={part.key} className="ck-countdown-block">
              <span className="ck-countdown-value">{part.value}</span>
              <span className="ck-countdown-unit">{part.unit}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'words') {
    const parts = [
      wordPart(left.days, 'day', 'days'),
      wordPart(left.hours, 'hour', 'hours'),
      wordPart(left.minutes, 'minute', 'minutes'),
      wordPart(left.seconds, 'second', 'seconds'),
    ];
    return (
      <div className="ck-countdown ck-countdown--words" role="timer" aria-live="off">
        {label ? <span className="ck-countdown-label">{label}</span> : null}
        <span className="ck-countdown-words" data-testid="countdown-words">
          {parts.join(', ')}
        </span>
      </div>
    );
  }

  return (
    <div className="ck-countdown" role="timer" aria-live="off">
      {label ? <span className="ck-countdown-label">{label}</span> : null}
      <span className="ck-countdown-clock">
        {left.days > 0 ? <b>{left.days}d </b> : null}
        <b>{pad(left.hours)}</b>:<b>{pad(left.minutes)}</b>:<b>{pad(left.seconds)}</b>
      </span>
    </div>
  );
};

export default PromoCountdown;
